import { createContextApi, providesOne, providesList, invalidatesOne, registerCrossContextInvalidation } from '@quickbee/store';

// src/index.ts
var GOALS_TAGS = ["Goal", "GoalProgress", "LinkableScheme"];
function invalidatesGoal(id) {
  return [
    ...invalidatesOne("Goal", id),
    { type: "GoalProgress", id },
    { type: "GoalProgress", id: "LIST" },
    { type: "LinkableScheme", id: "LIST" }
  ];
}
var goalsEndpoints = (build) => ({
  /**
   * `GET /goals` — the caller's own goals, optionally filtered by status.
   *
   * Resolves to a `PagedResult` because the response *envelope* is `PagedApiResponse`, but
   * the set is never actually paged (see `ListGoalsArgs`), so this endpoint deliberately
   * does not use `createCursorPagedEndpoint`: there is no cursor to follow, and merging
   * pages that can never arrive would only hide a backend change if one ever landed.
   */
  listGoals: build.query({
    query: (params) => ({ url: "/goals", params }),
    providesTags: (result) => providesList("Goal", result),
    // §10.4 transactional list: a saver's own goals, re-read whenever they open the tab.
    keepUnusedDataFor: 120
  }),
  getGoal: build.query({
    query: (id) => ({ url: `/goals/${id}` }),
    providesTags: (_r, _e, id) => providesOne("Goal", id)
  }),
  /**
   * `GET /goals/{id}/progress` — percent, remaining, on-track, and the next milestone.
   *
   * Provides the collective `GoalProgress/LIST` tag alongside its own id, because the
   * events that actually move progress (a capture, a gold credit) never name the goal: the
   * cross-context rules in `index.ts` can only invalidate broadly, and a per-id-only tag
   * would make those rules silent no-ops.
   */
  getGoalProgress: build.query({
    query: (id) => ({ url: `/goals/${id}/progress` }),
    providesTags: (_r, _e, id) => [
      { type: "GoalProgress", id },
      { type: "GoalProgress", id: "LIST" }
    ],
    // §10.4 financial balance: this is the number the saver is watching.
    keepUnusedDataFor: 60
  }),
  /**
   * `GET /goals/linkable-schemes` — wizard step 2. A plain list, not a page.
   *
   * One row per *enrollment*, so the same `schemeId` may repeat (ADR-011); rows are
   * therefore tagged by `schemeEnrollmentId ?? schemeId`, since tagging by `schemeId`
   * would collapse two genuinely distinct plans onto one cache entry.
   */
  listLinkableSchemes: build.query({
    query: (args) => ({
      url: "/goals/linkable-schemes",
      // Absent when creating. Sent when editing, so the plan the edited goal already funds is
      // reported linkable instead of "already linked" — to itself.
      ...args?.forGoalId ? { params: { forGoalId: args.forGoalId } } : {}
    }),
    providesTags: (result) => [
      { type: "LinkableScheme", id: "LIST" },
      ...(result ?? []).map((option) => ({
        type: "LinkableScheme",
        id: option.schemeEnrollmentId ?? option.schemeId
      }))
    ],
    // §10.4 reference data: the customer's plan set changes only when they enrol.
    keepUnusedDataFor: 900
  }),
  /**
   * `POST /goals` — single-shot wizard submission; responds with the created goal.
   *
   * `draftId` identifies the wizard session, not the goal (which has no id yet), so a
   * double-tap on "Create Goal" replays the first response instead of creating a second
   * goal and burning one of the customer's capped active-goal slots (INV-11).
   */
  createGoal: build.mutation({
    query: ({ draftId, ...body }) => ({
      url: "/goals",
      method: "POST",
      body,
      idempotencyKey: `goal-create:${draftId}`
    }),
    // The new goal takes a plan out of wizard step 2's picker — see `invalidatesGoal`.
    invalidatesTags: [
      { type: "Goal", id: "LIST" },
      { type: "LinkableScheme", id: "LIST" }
    ]
  }),
  /**
   * `PUT /goals/{id}` — **full replace**: any milestone left out is deleted, and any kept
   * milestone must echo back its id (INV-13). Send the whole edited goal.
   *
   * `editId` must be stable across retries of one save and different for the next save — a
   * value minted when the edit drawer opens is the natural source.
   */
  updateGoal: build.mutation({
    query: ({ id, editId, ...body }) => ({
      url: `/goals/${id}`,
      method: "PUT",
      body,
      idempotencyKey: `goal-update:${id}:${editId}`
    }),
    invalidatesTags: (_r, _e, { id }) => invalidatesGoal(id)
  }),
  /** `DELETE /goals/{id}` — soft delete. `204 No Content`, so this resolves with `undefined`. */
  deleteGoal: build.mutation({
    query: (id) => ({ url: `/goals/${id}`, method: "DELETE", idempotencyKey: `goal-delete:${id}` }),
    invalidatesTags: (_r, _e, id) => invalidatesGoal(id)
  }),
  /**
   * `POST /goals/{id}/abandon` — the customer's own "give up on this goal" action.
   *
   * A one-shot terminal transition (INV-15), so the goal id alone is a legitimately stable
   * key: a replayed abandon is the same abandon. Abandoning an already-terminal goal is
   * rejected with `GOAL_NOT_ACTIVE`.
   */
  abandonGoal: build.mutation({
    query: ({ id, ...body }) => ({
      url: `/goals/${id}/abandon`,
      method: "POST",
      body,
      idempotencyKey: `goal-abandon:${id}`
    }),
    invalidatesTags: (_r, _e, { id }) => invalidatesGoal(id)
  })
});

// src/index.ts
var goalsApi = createContextApi({
  context: "goals",
  reducerPath: "goalsApi",
  tagTypes: GOALS_TAGS,
  endpoints: goalsEndpoints
});
var GOALS_INVALIDATION_RULES = [
  {
    // The capture that precedes the credit. Published by `@quickbee/api-payment`.
    event: "payment.captured",
    invalidate: () => [
      { type: "GoalProgress", id: "LIST" },
      { type: "Goal", id: "LIST" }
    ]
  },
  {
    // Enrolling adds a linkable plan to wizard step 2 — the one goals list that is
    // reference-cached for 15 minutes and would otherwise not show the new plan.
    // Published by `@quickbee/api-scheme`'s `createEnrollment`.
    event: "enrollment.created",
    invalidate: () => [{ type: "LinkableScheme", id: "LIST" }]
  }
];
function registerGoalsInvalidation(store) {
  return registerCrossContextInvalidation({
    store,
    api: goalsApi,
    rules: GOALS_INVALIDATION_RULES
  });
}
var {
  useListGoalsQuery,
  useGetGoalQuery,
  useGetGoalProgressQuery,
  useListLinkableSchemesQuery,
  useCreateGoalMutation,
  useUpdateGoalMutation,
  useDeleteGoalMutation,
  useAbandonGoalMutation
} = goalsApi;

export { GOALS_INVALIDATION_RULES, GOALS_TAGS, goalsApi, registerGoalsInvalidation, useAbandonGoalMutation, useCreateGoalMutation, useDeleteGoalMutation, useGetGoalProgressQuery, useGetGoalQuery, useListGoalsQuery, useListLinkableSchemesQuery, useUpdateGoalMutation };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map